/**
 * Date utilities for handling date-only values consistently across timezones.
 * 
 * Problem: JavaScript Date objects include time and timezone, causing off-by-one
 * day errors when storing date-only values like training block start/end dates.
 * 
 * Solution: Store dates at midnight UTC and always format using UTC-aware functions.
 * This ensures the same calendar date is displayed regardless of user's timezone.
 */

/**
 * Create a Date object at midnight UTC from a YYYY-MM-DD string.
 * Use this when saving date-only values to the database.
 */
export function dateStringToUTC(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

/**
 * Create a Date object at midnight UTC from year, month, day components.
 * Use this when creating date-only values programmatically.
 */
export function createUTCDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
}

/**
 * Format a UTC date as YYYY-MM-DD string for form inputs.
 * Use this when populating date input values from stored dates.
 */
export function formatDateForInput(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a UTC date for display (e.g., "Jan 15").
 * Always uses UTC to avoid timezone shifts.
 */
export function formatDateDisplay(date: Date | string, formatStr: 'short' | 'medium' | 'long' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const day = d.getUTCDate();
  const month = d.getUTCMonth();
  const year = d.getUTCFullYear();
  
  switch (formatStr) {
    case 'short':
      return `${months[month]} ${day}`;
    case 'medium':
      return `${months[month]} ${day}, ${year}`;
    case 'long':
      return `${monthsFull[month]} ${day}, ${year}`;
    default:
      return `${months[month]} ${day}`;
  }
}

/**
 * Get UTC date components from a date.
 */
export function getUTCDateParts(date: Date | string): { year: number; month: number; day: number } {
  const d = typeof date === 'string' ? new Date(date) : date;
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
  };
}

/**
 * Add weeks to a UTC date, returning a new UTC date.
 */
export function addWeeksUTC(date: Date | string, weeks: number): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  const newDate = new Date(d.getTime());
  newDate.setUTCDate(newDate.getUTCDate() + (weeks * 7));
  return newDate;
}

/**
 * Add days to a UTC date, returning a new UTC date.
 */
export function addDaysUTC(date: Date | string, days: number): Date {
  const d = typeof date === 'string' ? new Date(date) : date;
  const newDate = new Date(d.getTime());
  newDate.setUTCDate(newDate.getUTCDate() + days);
  return newDate;
}

/**
 * Calculate difference in days between two UTC dates.
 */
export function differenceInDaysUTC(dateA: Date | string, dateB: Date | string): number {
  const a = typeof dateA === 'string' ? new Date(dateA) : dateA;
  const b = typeof dateB === 'string' ? new Date(dateB) : dateB;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((a.getTime() - b.getTime()) / msPerDay);
}

/**
 * Calculate difference in weeks between two UTC dates.
 */
export function differenceInWeeksUTC(dateA: Date | string, dateB: Date | string): number {
  return Math.floor(differenceInDaysUTC(dateA, dateB) / 7);
}

/**
 * Check if a date is today (comparing UTC dates).
 */
export function isToday(date: Date | string): boolean {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  return d.getUTCFullYear() === now.getUTCFullYear() &&
         d.getUTCMonth() === now.getUTCMonth() &&
         d.getUTCDate() === now.getUTCDate();
}

/**
 * Get today's date at midnight UTC.
 */
export function getTodayUTC(): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0));
}

/**
 * Compare two dates (UTC) - returns -1 if a < b, 0 if equal, 1 if a > b.
 */
export function compareDatesUTC(dateA: Date | string, dateB: Date | string): number {
  const a = typeof dateA === 'string' ? new Date(dateA) : dateA;
  const b = typeof dateB === 'string' ? new Date(dateB) : dateB;
  
  const aTime = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bTime = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  
  if (aTime < bTime) return -1;
  if (aTime > bTime) return 1;
  return 0;
}

/**
 * Check if a date is within an interval (inclusive, using UTC dates).
 */
export function isWithinIntervalUTC(date: Date | string, start: Date | string, end: Date | string): boolean {
  return compareDatesUTC(date, start) >= 0 && compareDatesUTC(date, end) <= 0;
}
